import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Plus } from 'lucide-react'
import { createClient } from '@/lib/supabase/client'
import { useEffect, useState } from 'react'
import { UserWithCompany } from '@/types/database'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'

export default function UsuariosPage() {
  const [users, setUsers] = useState<UserWithCompany[]>([])
  const [loading, setLoading] = useState(true)
  const navigate = useNavigate()
  const supabase = createClient()

  useEffect(() => {
    async function loadUsers() {
      try {
        const { data: { user } } = await supabase.auth.getUser()
        
        if (!user) {
          navigate('/auth/login')
          return
        }

        // Verificar se o usuário é admin
        const { data: userData, error: userError } = await supabase
          .from('users')
          .select('role, company_id')
          .eq('id', user.id)
          .single()

        if (userError || userData.role !== 'admin') {
          toast.error('Acesso não autorizado')
          navigate('/dashboard')
          return
        }

        // Carregar usuários da mesma empresa
        const { data: usersData, error: usersError } = await supabase
          .from('users')
          .select(`
            *,
            company:companies(*)
          `)
          .eq('company_id', userData.company_id)

        if (usersError) throw usersError

        setUsers(usersData)
      } catch (error) {
        console.error(error)
        toast.error('Erro ao carregar usuários')
      } finally {
        setLoading(false)
      }
    }

    loadUsers()
  }, [supabase, navigate])

  if (loading) {
    return <div>Carregando...</div>
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h2 className="text-3xl font-bold tracking-tight">Usuários</h2>
          <p className="text-muted-foreground">
            Gerencie os usuários da sua empresa
          </p>
        </div>
        <Button className="w-full md:w-auto" onClick={() => navigate('/dashboard/usuarios/novo')}>
          <Plus className="mr-2 h-4 w-4" />
          Novo Usuário
        </Button>
      </div>

      <div className="rounded-md border">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b bg-muted/50">
              <tr>
                <th className="h-10 px-4 text-left align-middle font-medium">Nome</th>
                <th className="h-10 px-4 text-left align-middle font-medium">Email</th>
                <th className="h-10 px-4 text-left align-middle font-medium">Função</th>
                <th className="h-10 px-4 text-left align-middle font-medium">Status</th>
                <th className="h-10 px-4 text-left align-middle font-medium">Ações</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr key={user.id} className="border-b transition-colors hover:bg-muted/50">
                  <td className="p-4">{user.name}</td>
                  <td className="p-4">{user.email}</td>
                  <td className="p-4 capitalize">{user.role}</td>
                  <td className="p-4">
                    <span className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ${
                      user.active ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
                    }`}>
                      {user.active ? 'Ativo' : 'Inativo'}
                    </span>
                  </td>
                  <td className="p-4">
                    <Button variant="ghost" size="sm" onClick={() => navigate(`/dashboard/usuarios/${user.id}`)}>
                      Detalhes
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
} 